import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import geolocationService, { MOROCCAN_CITIES, PRICE_REGIONS } from '../services/geolocation.service';

// Create the context
const LocationContext = createContext(null);

// Debug logging
const DEBUG = true;
const log = (...args) => DEBUG && console.log('[LocationContext]', ...args);

// Provider component
export const LocationProvider = ({ children }) => {
    const [currentCity, setCurrentCity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [priceRegion, setPriceRegion] = useState(PRICE_REGIONS.FES); // Default to Fes (regular price)
    const [notification, setNotification] = useState(null); // For showing city change notifications
    const [error, setError] = useState(null);

    // Show notification for 3 seconds
    const showNotification = useCallback((message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    }, []);

    // Detect location on mount
    useEffect(() => {
        const detectLocation = async () => {
            try {
                setError(null);
                const city = await geolocationService.detectCity();
                setCurrentCity(city);
                setPriceRegion(city?.region || PRICE_REGIONS.FES);
            } catch (error) {
                console.error('Error detecting location:', error);
                setError('Impossible de détecter votre localisation');
                // Default to Fes region if detection fails
                const defaultCity = MOROCCAN_CITIES.find(c => c.name === 'Fès');
                setCurrentCity(defaultCity);
                setPriceRegion(PRICE_REGIONS.FES);
            } finally {
                setLoading(false);
            }
        };

        detectLocation();
    }, []);

    // Change city manually
    const changeCity = useCallback((cityId) => {
        log('changeCity called with cityId:', cityId);
        try {
            const city = geolocationService.setCity(cityId);
            log('City found:', city);
            if (city) {
                const oldRegion = priceRegion;
                const newRegion = city.region || PRICE_REGIONS.FES;
                log('Changing region from', oldRegion, 'to', newRegion);
                
                setCurrentCity(city);
                setPriceRegion(newRegion);
                setError(null);
                
                // Log region change (no notifications)
                if (oldRegion !== newRegion) {
                    log('Region CHANGED! Prices will update.');
                }
            } else {
                log('City not found for id:', cityId);
                setError('Ville non trouvée');
            }
        } catch (err) {
            console.error('Error changing city:', err);
            setError('Erreur lors du changement de ville');
        }
    }, [priceRegion]);

    // Get the correct price for a product based on location
    const getPrice = useCallback((product) => {
        if (!product) return 0;
        
        // If in Casa region and price_casa exists and is greater than 0, use it
        const priceCasa = parseFloat(product.price_casa || 0);
        if (priceRegion === PRICE_REGIONS.CASA && priceCasa > 0) {
            return priceCasa;
        }
        
        // Otherwise use regular price
        return parseFloat(product.price || 0);
    }, [priceRegion]);

    // Format price with DH
    const formatPrice = useCallback((price) => {
        return parseFloat(price || 0).toFixed(2);
    }, []);

    // Get formatted price for a product
    const getFormattedPrice = useCallback((product) => {
        const price = getPrice(product);
        return formatPrice(price);
    }, [getPrice, formatPrice]);

    // Check if we're in Casa region - this will update when priceRegion changes
    const isCasaRegion = priceRegion === PRICE_REGIONS.CASA;
    
    // Log when isCasaRegion changes
    useEffect(() => {
        log('isCasaRegion changed to:', isCasaRegion, '(priceRegion:', priceRegion, ')');
    }, [isCasaRegion, priceRegion]);

    // Get region display name
    const getRegionName = useCallback(() => {
        return priceRegion === PRICE_REGIONS.CASA ? 'Casablanca' : 'Fès';
    }, [priceRegion]);

    // Get all cities
    const cities = MOROCCAN_CITIES;

    // Use useMemo to ensure context value reference changes when state changes
    const value = useMemo(() => {
        log('Creating new context value - priceRegion:', priceRegion, 'isCasaRegion:', isCasaRegion);
        return {
            currentCity,
            loading,
            priceRegion,
            isCasaRegion,
            cities,
            changeCity,
            getPrice,
            formatPrice,
            getFormattedPrice,
            notification,
            error,
            getRegionName,
        };
    }, [currentCity, loading, priceRegion, isCasaRegion, changeCity, getPrice, formatPrice, getFormattedPrice, notification, error, getRegionName]);

    return (
        <LocationContext.Provider value={value}>
            {/* Notification Toast */}
            {notification && (
                <div 
                    className={`location-notification ${notification.type}`}
                    style={{
                        position: 'fixed',
                        top: '100px',
                        right: '20px',
                        padding: '16px 24px',
                        borderRadius: '8px',
                        backgroundColor: notification.type === 'error' ? '#ff4444' : 
                                        notification.type === 'info' ? '#2196F3' : '#4CAF50',
                        color: 'white',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 9999,
                        animation: 'slideIn 0.3s ease',
                        fontWeight: '500',
                        fontSize: '14px',
                    }}
                >
                    {notification.message}
                </div>
            )}
            {children}
        </LocationContext.Provider>
    );
};

// Custom hook to use location context
export const useLocation = () => {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('useLocation must be used within a LocationProvider');
    }
    return context;
};

// Helper hook just for pricing
export const usePricing = () => {
    const { getPrice, formatPrice, getFormattedPrice, isCasaRegion, priceRegion } = useLocation();
    return { getPrice, formatPrice, getFormattedPrice, isCasaRegion, priceRegion };
};

export default LocationContext;
